import { computeRunModifiers } from "./field-mods";
import { generateRoster, nextReserveName } from "./operators";
import { generateRunMap, nodeById, nodesInColumn } from "./run-map";
import { BASE_SQUAD_SIZE, RUN_COLUMNS, type NodeKind, type RunNode, type RunState } from "./run-types";
import { hasPerk } from "./unlocks";

// Pure run lifecycle: every transition takes a RunState and returns a new
// one. Persistence and screens live elsewhere; this module is the rules.

// `deploy` comes from the profile roster (flow); tests and legacy paths fall
// back to a seeded draw. `campaign` carries the world state the Operation
// deploys into (meta/campaign.ts); omitted = a quiet theater with every
// tasking authorized.
export function createRun(
  seed: number,
  ownedUnlockIds: readonly string[],
  deploy?: { roster: string[]; reserves: string[] },
  theater: RunState["theater"] = "stone-wall",
  campaign?: { escalation?: number; allowedKinds?: ReadonlySet<NodeKind> },
): RunState {
  const squadMax = hasPerk(ownedUnlockIds, "fifth-man") ? BASE_SQUAD_SIZE + 1 : BASE_SQUAD_SIZE;
  const roster = deploy ? deploy.roster.slice(0, squadMax) : generateRoster(seed, squadMax);
  const reserves = deploy
    ? deploy.reserves.filter((n) => !roster.includes(n))
    : [];
  return {
    seed,
    theater,
    nodes: generateRunMap(seed, campaign?.allowedKinds),
    currentNodeId: null,
    clearedNodeIds: [],
    squadSize: roster.length,
    squadMax,
    roster,
    fallen: [],
    wipeLosses: [],
    reserves,
    modIds: [],
    opsEarned: 0,
    kills: 0,
    escalation: Math.max(0, campaign?.escalation ?? 0),
    outcome: "active",
  };
}

// Nodes the player may enter right now: column 0 before the first mission,
// otherwise the current node's forward edges.
export function availableNodes(run: RunState): RunNode[] {
  if (run.outcome !== "active") return [];
  if (run.currentNodeId === null) return nodesInColumn(run.nodes, 0);
  const current = nodeById(run.nodes, run.currentNodeId);
  if (!current) return [];
  return current.next
    .map((id) => nodeById(run.nodes, id))
    .filter((n): n is RunNode => n !== undefined);
}

export function isNodeAvailable(run: RunState, nodeId: string): boolean {
  return availableNodes(run).some((n) => n.id === nodeId);
}

// Ops awarded for clearing one node. Elites pay extra; friendly fire is a
// hard deduction (docs/design/GDD.md §Scoring); the finale pays out on run
// completion instead (see completeMission).
function opsForClear(node: RunNode, kills: number, ffIncidents: number): number {
  let ops = 2 + Math.floor(kills / 3);
  if (node.kind === "elite") ops += 2;
  // Specialist tasking pays a premium
  if (node.kind === "defend" || node.kind === "destroy" || node.kind === "ambush" || node.kind === "delay" || node.kind === "sead" || node.kind === "extraction" || node.kind === "rescue") ops += 1;
  return Math.max(0, ops - ffIncidents);
}

export type MissionOutcome = {
  run: RunState;
  // What the flow should show next.
  next: "draft" | "run-victory" | "run-defeat";
  opsAwarded: number;
};

export type MissionCompletion = {
  success: boolean;
  kills: number;
  squadAlive: number;
  // Names of the operators who walked out — the roster carries identities,
  // not counts. Falls back to trimming the roster when absent (tests, legacy).
  survivors?: string[];
  ffIncidents?: number;
};

// Apply a finished combat mission (the player fought node `nodeId`).
export function completeMission(run: RunState, nodeId: string, result: MissionCompletion): MissionOutcome {
  const node = nodeById(run.nodes, nodeId);
  if (!node) throw new Error(`completeMission: unknown node ${nodeId}`);

  const kills = run.kills + result.kills;
  const ffIncidents = result.ffIncidents ?? 0;

  if (!result.success) {
    const opsAwarded = Math.max(0, Math.floor(kills / 3) - ffIncidents); // consolation: kills still pay
    const survivorsOnDefeat = result.survivors ?? [];
    const lostInWipe = run.roster.filter((n) => !survivorsOnDefeat.includes(n));
    const failed: RunState = {
      ...run,
      currentNodeId: nodeId,
      roster: survivorsOnDefeat,
      fallen: [...run.fallen, ...lostInWipe],
      // The run-ending losses are the permanent ones (profile KIA)
      wipeLosses: lostInWipe,
      kills,
      opsEarned: run.opsEarned + opsAwarded,
      outcome: "defeat",
    };
    return { run: failed, next: "run-defeat", opsAwarded };
  }

  const squadSize = Math.max(1, Math.min(run.squadMax, result.squadAlive));
  const roster = result.survivors && result.survivors.length > 0
    ? result.survivors.slice(0, squadSize)
    : run.roster.slice(0, squadSize);
  const fallen = [...run.fallen, ...run.roster.filter((n) => !roster.includes(n))];

  const opsAwarded = opsForClear(node, result.kills, ffIncidents) + (node.kind === "final" ? 6 : 0);
  const cleared: RunState = {
    ...run,
    currentNodeId: nodeId,
    clearedNodeIds: [...run.clearedNodeIds, nodeId],
    // Survivors carry forward — losses are permanent until a resupply node.
    squadSize: roster.length,
    roster,
    fallen,
    kills,
    opsEarned: run.opsEarned + opsAwarded,
    outcome: node.kind === "final" ? "victory" : "active",
  };

  return {
    run: cleared,
    next: node.kind === "final" ? "run-victory" : "draft",
    opsAwarded,
  };
}

// A replacement operator is a new name, never a resurrection — ready
// reserves from the FOB first, seeded fill-ins if the bench is empty.
function withReplacement(run: RunState): { roster: string[]; reserves: string[] } {
  const reserve = run.reserves[0];
  if (reserve !== undefined) {
    return { roster: [...run.roster, reserve], reserves: run.reserves.slice(1) };
  }
  return {
    roster: [...run.roster, nextReserveName(run.seed, [...run.roster, ...run.fallen])],
    reserves: run.reserves,
  };
}

// Resupply nodes skip the engine entirely: +1 squad member, small ops trickle.
export function applyResupply(run: RunState, nodeId: string): RunState {
  const node = nodeById(run.nodes, nodeId);
  if (!node || node.kind !== "resupply") throw new Error(`applyResupply: ${nodeId} is not a resupply node`);
  // The roster is the source of truth for who's standing; squadSize mirrors it.
  const next = run.roster.length < run.squadMax
    ? withReplacement(run)
    : { roster: run.roster, reserves: run.reserves };
  return {
    ...run,
    currentNodeId: nodeId,
    clearedNodeIds: [...run.clearedNodeIds, nodeId],
    squadSize: next.roster.length,
    roster: next.roster,
    reserves: next.reserves,
    opsEarned: run.opsEarned + 1,
  };
}

export function addDraftedMod(run: RunState, modId: string): RunState {
  if (run.modIds.includes(modId)) return run;
  const next: RunState = { ...run, modIds: [...run.modIds, modId] };
  // An ATTACHMENT-style mod raises the cap and fills the slot immediately.
  const bonus = computeRunModifiers(next.modIds).squadBonusMembers;
  const baseMax = run.squadMax - computeRunModifiers(run.modIds).squadBonusMembers;
  next.squadMax = baseMax + bonus;
  if (next.squadMax > run.squadMax) {
    const drawn = withReplacement(run);
    next.roster = drawn.roster;
    next.reserves = drawn.reserves;
    next.squadSize = next.roster.length;
  }
  return next;
}

export function runDepth(run: RunState): number {
  if (run.currentNodeId === null) return 0;
  return (nodeById(run.nodes, run.currentNodeId)?.col ?? -1) + 1;
}

export function isFinalColumn(node: RunNode): boolean {
  return node.col === RUN_COLUMNS - 1;
}
